import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Loader2, Send, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useMyEnrollmentRequests, useMyEnrollments, useSubmitEnrollmentRequest, useValidateJoinCode } from '@/hooks/useSchoolApi';
import { normalizeJoinCode } from '@/lib/joinCode';

const STATUS_PRIORITY: Record<string, number> = {
  enrolled: 6,
  completed: 5,
  approved: 4,
  pending: 3,
  rejected: 2,
  withdrawn: 1,
};

const STATUS_CONFIG: Record<string, { icon: typeof Clock; label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  pending: { icon: Clock, label: 'Pending', variant: 'secondary' },
  approved: { icon: CheckCircle, label: 'Approved', variant: 'default' },
  rejected: { icon: XCircle, label: 'Rejected', variant: 'destructive' },
  enrolled: { icon: GraduationCap, label: 'Enrolled', variant: 'default' },
  completed: { icon: CheckCircle, label: 'Completed', variant: 'default' },
  withdrawn: { icon: XCircle, label: 'Withdrawn', variant: 'outline' },
};

interface MergedRecord {
  id: string;
  cohort_id: string;
  status: string;
  type: 'enrollment' | 'request';
}

export function PassportSchoolSection() {
  const [codeInput, setCodeInput] = useState('');
  const normalized = normalizeJoinCode(codeInput);

  const { data: validation, isLoading: validating } = useValidateJoinCode(codeInput);
  const { data: myRequests } = useMyEnrollmentRequests();
  const { data: myEnrollments } = useMyEnrollments();
  const submitRequest = useSubmitEnrollmentRequest();

  // Merge requests + enrollments by cohort_id, keep highest-priority status
  const mergedRecords = useMemo(() => {
    const map = new Map<string, MergedRecord>();

    myRequests?.forEach(r => {
      const existing = map.get(r.cohort_id);
      const priority = STATUS_PRIORITY[r.status] ?? 0;
      if (!existing || priority > (STATUS_PRIORITY[existing.status] ?? 0)) {
        map.set(r.cohort_id, { id: r.id, cohort_id: r.cohort_id, status: r.status, type: 'request' });
      }
    });

    myEnrollments?.forEach(e => {
      const existing = map.get(e.cohort_id);
      const priority = STATUS_PRIORITY[e.status] ?? 0;
      if (!existing || priority > (STATUS_PRIORITY[existing.status] ?? 0)) {
        map.set(e.cohort_id, { id: e.id, cohort_id: e.cohort_id, status: e.status, type: 'enrollment' });
      }
    });

    return Array.from(map.values());
  }, [myRequests, myEnrollments]);

  const hasPendingForCohort = (cohortId: string) =>
    mergedRecords.some(r => r.cohort_id === cohortId && r.status === 'pending');

  const isEnrolledInCohort = (cohortId: string) =>
    mergedRecords.some(r => r.cohort_id === cohortId && (r.status === 'enrolled' || r.status === 'completed'));

  const isCodeComplete = normalized.length === 9; // XXXX-XXXX

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">School & Program</CardTitle>
        </div>
        <CardDescription>Enter your cohort join code to request enrollment.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Unified enrollment / request list */}
        {mergedRecords.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Your Enrollments</p>
            {mergedRecords.map(record => {
              const cfg = STATUS_CONFIG[record.status];
              const Icon = cfg?.icon || Clock;
              return (
                <div key={record.id} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      {record.status === 'pending' ? 'Enrollment request pending' : 'School Enrollment'}
                    </span>
                  </div>
                  <Badge variant={cfg?.variant || 'outline'}>
                    {cfg?.label || record.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}

        {/* Join Code Entry */}
        <div className="space-y-3">
          <p className="text-sm font-medium">Enter Join Code</p>
          <Input
            placeholder="e.g. ABCD-1234"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value)}
            maxLength={10}
            className="font-mono tracking-wider text-center text-lg"
          />

          {validating && isCodeComplete && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {isCodeComplete && !validating && !validation && (
            <p className="text-sm text-destructive text-center">Invalid or inactive join code.</p>
          )}

          {validation && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <p className="text-sm font-medium">{validation.school_name}</p>
              <p className="text-xs text-muted-foreground">
                {validation.program_name} — {validation.cohort_name}
              </p>

              {isEnrolledInCohort(validation.cohort_id) ? (
                <Badge variant="default">Already Enrolled</Badge>
              ) : hasPendingForCohort(validation.cohort_id) ? (
                <Badge variant="secondary">Request Pending</Badge>
              ) : (
                <Button
                  size="sm"
                  disabled={submitRequest.isPending}
                  onClick={() => submitRequest.mutate({ joinCode: normalized })}
                  className="w-full"
                >
                  {submitRequest.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Request Enrollment
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
